import { createContext, useState } from "react";
import { Task } from "../interface/Task";
import { TimeInterface, displayTime } from "../interface/Time";
import { BEGIN_TIME, DEFAULT_AVAILABLE_POINTS, DEFAULT_DATA } from "../constants/constants";
import { computeScore, computeValues } from "../constants/formulas";
import { Data } from "../interface/Data";
import { GameState } from "../interface/GameState";
import { Values } from "../interface/Values";
import { CustomEvent, SpontaneousEvent } from "../interface/CustomEvent"
import { Timestamp } from "../interface/Timestamp";
import { WeatherState } from "../components/Game/Interface/Weather/WeatherUtils";
import { Bounds, DEFAULT_BOUNDS } from "../interface/Bounds";
import { InterfaceParameters } from "../interface/InterfaceConfig";
import { Scales } from "../interface/Scales";

interface Props {
    children: React.ReactNode
}


/**
 * This interface encapsulates all the states associated to the game.
 */
export interface IGameContext {
    /**
     * Current state of the game (Round, Result, End or Score).
     */
    gameState: GameState,

    /**
     * Current round of the game (0 to <MAX ROUND>).
     */
    round: number,

    /**
     * Number of rounds per game.
     */
    maxRound: number,

    /**
     * Min and max values of data
     */
    bounds: Bounds,

    /**
     * Conversion of game units to real units.
     */
    scales: Scales,

    /**
     * Values of the parameters.
     */
    data: Data,

    /**
     * Values of the bottom row.
     */
    values: Values,

    /**
     * Number of points to allocate.
     */
    points: number,

    maxPoints: number[],

    /**
     * Array of timestamps to keep track of values and scores.
     */
    scores: Timestamp[][],

    /**
     * Array of final scores. As opposed to the previous variable,
     * the scores are aggregated for each round.
     */
    finalScores: number[],

    /**
     * Length of a step, in minutes.
     */
    stepDuration: number,

    /**
     * Current step, based on the current time.
     */
    currentStep: number,

    /**
     * Time at which round starts per round.
     */
    beginTime: TimeInterface[],

    /**
     * Time at which round ends per round.
     */
    endTime: TimeInterface[],

    /**
     * Current display time.
     */
    time: TimeInterface,

    /**
     * Value of interior temperature.
     */
    tempIn: number,

    /**
     * Array of exterior temperatures for each round.
     */
    tempExt: number[][],

    /**
     * 
     */
    nPeople: number[][],

    /**
     * Array of weather states for each round.
     */
    weatherIdx: WeatherState[][],

    /**
     * Events that will occur during the round.
     */
    events: CustomEvent[][],

    /**
     * Tasks to achieve during the round.
     */
    tasks: Task[],

    /**
     * Events that will pop up during the round.
     */
    spontaneousEvents: SpontaneousEvent[][],

    /**
     * Configuration file for the interface.
     */
    interfaceParams: InterfaceParameters | null,

    /**
     * Values at the start of each round.
     */
    defaultValues: Data[],

    /**
     * If true, disables the nextRound button.
     */
    disableAdvanceRound: boolean,

    results: any,

    startAt: Date,
    gameId: string,
    setGameId: React.Dispatch<React.SetStateAction<string>>,
    setGameState: React.Dispatch<React.SetStateAction<GameState>>,
    setBounds: React.Dispatch<React.SetStateAction<Bounds>>,
    setScales: React.Dispatch<React.SetStateAction<Scales>>,
    setData: React.Dispatch<React.SetStateAction<Data>>,
    setValues: React.Dispatch<React.SetStateAction<Values>>,
    setRound: React.Dispatch<React.SetStateAction<number>>,
    setMaxPoints: React.Dispatch<React.SetStateAction<number[]>>,
    setMaxRound: React.Dispatch<React.SetStateAction<number>>,
    setPoints: React.Dispatch<React.SetStateAction<number>>,
    setScores: React.Dispatch<React.SetStateAction<Timestamp[][]>>,
    setFinalScores: React.Dispatch<React.SetStateAction<number[]>>,
    setStepDuration: React.Dispatch<React.SetStateAction<number>>,
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>,
    setTime: React.Dispatch<React.SetStateAction<TimeInterface>>,
    setBeginTime: React.Dispatch<React.SetStateAction<TimeInterface[]>>,
    setEndTime: React.Dispatch<React.SetStateAction<TimeInterface[]>>,
    setTempIn: React.Dispatch<React.SetStateAction<number>>,
    setTempExt: React.Dispatch<React.SetStateAction<number[][]>>,
    setNPeople: React.Dispatch<React.SetStateAction<number[][]>>,
    setWeatherIdx: React.Dispatch<React.SetStateAction<WeatherState[][]>>,
    setEvents: React.Dispatch<React.SetStateAction<CustomEvent[][]>>,
    setTasks: React.Dispatch<React.SetStateAction<Task[]>>,
    setSpontaneousEvents: React.Dispatch<React.SetStateAction<SpontaneousEvent[][]>>,
    setInterfaceParams: React.Dispatch<React.SetStateAction<InterfaceParameters | null>>,
    setDefaultValues: React.Dispatch<React.SetStateAction<Data[]>>,
    setDisableAdvanceRound: React.Dispatch<React.SetStateAction<boolean>>,
    setResults: React.Dispatch<React.SetStateAction<any>>,
    setStartAt: React.Dispatch<React.SetStateAction<Date>>
}

export const GameContext = createContext<IGameContext>(
    {
        gameState: GameState.INSTRUCTIONS,
        bounds: DEFAULT_BOUNDS,
        scales: {...DEFAULT_DATA, "energy": 1},
        data: DEFAULT_DATA,
        values: {...computeValues(DEFAULT_DATA), energie_tot:0},
        round: 0,
        maxRound: 0,
        points: 0,
        maxPoints: [],
        scores: [],
        finalScores: [],
        stepDuration: 0,
        currentStep: 0,
        time: { hours: 0, minutes: 0 },
        beginTime: [],
        endTime: [],
        tempIn: 0,
        tempExt: [],
        weatherIdx: [],
        events: [],
        nPeople: [],
        tasks: [],
        spontaneousEvents: [],
        interfaceParams: null,
        defaultValues: [],
        disableAdvanceRound: false,
        results: [],
        startAt: new Date(),
        gameId: "",
        setGameId: () => { /* *Does nothing.* */ },
        setGameState: () => { /* *Does nothing.* */ },
        setBounds: () => { /* *Does nothing.* */ },
        setScales: () => { /* *Does nothing.* */ },
        setData: () => { /* *Does nothing.* */ },
        setMaxPoints: () => { /* *Does nothing.* */ },
        setValues: () => { /* *Does nothing.* */ },
        setRound: () => { /* *Does nothing.* */ },
        setMaxRound: () => { /* *Does nothing.* */ },
        setPoints: () => { /* *Does nothing.* */ },
        setScores: () => { /* *Does nothing.* */ },
        setFinalScores: () => { /* *Does nothing.* */ },
        setStepDuration: () => { /* *Does nothing.* */ },
        setCurrentStep: () => { /* *Does nothing.* */ },
        setTime: () => {  /* Does nothing, */},
        setBeginTime: () => { /* *Does nothing.* */ },
        setEndTime: () => { /* *Does nothing.* */ },
        setTempIn: () => { /* *Does nothing.* */ },
        setTempExt: () => { /* *Does nothing.* */ },
        setNPeople: () => { /* *Does nothing.* */ },
        setWeatherIdx: () => { /* *Does nothing.* */ },
        setEvents: () => { /* *Does nothing.* */ },
        setTasks: () => { /* *Does nothing.* */ },
        setSpontaneousEvents: () => { /* *Does nothing.* */ },
        setInterfaceParams: () => { /* *Does nothing.* */ },
        setDefaultValues: () => { /* *Does nothing.* */ },
        setDisableAdvanceRound: () => { /* *Does nothing.* */ },
        setResults: () => { /* *Does nothing.* */ },
        setStartAt: () => { /* *Does nothing.* */ }
    }
);


export const GameContextProvider = ({ children }: Props) => {
    const [gameState, setGameState] = useState<GameState>(GameState.INSTRUCTIONS);
    const [round, setRound] = useState<number>(0);
    const [maxRound, setMaxRound] = useState<number>(0);

    const [bounds, setBounds] = useState<Bounds>(DEFAULT_BOUNDS);
    const [scales, setScales] = useState<Scales>({...DEFAULT_DATA, "energy": 1});
    const [data, setData] = useState<Data>(DEFAULT_DATA);
    const [values, setValues] = useState<Values>({...computeValues(data),energie_tot:0});
    const [points, setPoints] = useState<number>(DEFAULT_AVAILABLE_POINTS);

    const [scores, setScores] = useState<Timestamp[][]>([[]]);
    const [finalScores, setFinalScores] = useState<number[]>([]);

    const [stepDuration, setStepDuration] = useState<number>(0);
    const [currentStep, setCurrentStep] = useState<number>(0);

    const [time, setTime] = useState<TimeInterface>(BEGIN_TIME);
    const [beginTime, setBeginTime] = useState<TimeInterface[]>([]);
    const [endTime, setEndTime] = useState<TimeInterface[]>([]);

    const [tempIn, setTempIn] = useState<number>(DEFAULT_DATA.temp);
    const [tempExt, setTempExt] = useState<number[][]>([[]]);
    const [weatherIdx, setWeatherIdx] = useState<WeatherState[][]>([[]]);

    const [nPeople, setNPeople] = useState<number[][]>([[]]);

    const [events, setEvents] = useState<CustomEvent[][]>([[]]);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [spontaneousEvents, setSpontaneousEvents] = useState<SpontaneousEvent[][]>([[]]);

    const [interfaceParams, setInterfaceParams] = useState<InterfaceParameters | null>(null);

    const [disableAdvanceRound, setDisableAdvanceRound] = useState<boolean>(false);

    const [defaultValues, setDefaultValues] = useState<Data[]>([]);
    const [results, setResults] = useState<any>([{ "timestamps": [], "score": 0 }]);

    const [maxPoints, setMaxPoints] = useState<number[]>([]);

    const [startAt, setStartAt] = useState<Date>(new Date());

    const [gameId, setGameId] = useState<string>(crypto.randomUUID());

    return (
        <GameContext.Provider value={{
            startAt: startAt,
            setStartAt: setStartAt,
            gameState: gameState,
            bounds: bounds,
            data: data,
            scales: scales,
            values: values,
            round: round,
            maxPoints: maxPoints,
            maxRound: maxRound,
            points: points,
            scores: scores,
            finalScores: finalScores,
            stepDuration: stepDuration,
            currentStep: currentStep,
            time: time,
            beginTime: beginTime,
            endTime: endTime,
            nPeople: nPeople,
            tempIn: tempIn,
            tempExt: tempExt,
            weatherIdx: weatherIdx,
            events: events,
            tasks: tasks,
            spontaneousEvents: spontaneousEvents,
            interfaceParams: interfaceParams,
            defaultValues: defaultValues,
            disableAdvanceRound: disableAdvanceRound,
            results: results,
            setGameState: setGameState,
            setBounds: setBounds,
            setScales: setScales,
            setData: setData,
            setValues: setValues,
            setRound: setRound,
            setMaxRound: setMaxRound,
            setPoints: setPoints,
            setMaxPoints: setMaxPoints,
            setScores: setScores,
            setFinalScores: setFinalScores,
            setStepDuration: setStepDuration,
            setCurrentStep: setCurrentStep,
            setTime: setTime,
            setBeginTime: setBeginTime,
            setEndTime: setEndTime,
            setTempIn: setTempIn,
            setTempExt: setTempExt,
            setNPeople: setNPeople,
            setWeatherIdx: setWeatherIdx,
            setEvents: setEvents,
            setTasks: setTasks,
            setSpontaneousEvents: setSpontaneousEvents,
            setInterfaceParams: setInterfaceParams,
            setDefaultValues: setDefaultValues,
            setDisableAdvanceRound: setDisableAdvanceRound,
            setResults: setResults,
            gameId: gameId,
            setGameId: setGameId
        }}>
            {children}
        </GameContext.Provider>
    );
}
